/**
 * TabBoundary.jsx
 * Keeps one broken tab from taking the whole inbox with it.
 *
 * This exists because consolidating four screens into one changed what a crash
 * costs. When Approvals was its own page, a bug there broke Approvals. Now
 * that it is a tab, the same bug blanks the screen you answer customers on —
 * and React's default for an uncaught render error is to unmount everything.
 *
 * It was not hypothetical: Reviews.jsx read `data.links.length` without
 * guarding `links`, and a response without that key emptied the page. That
 * particular bug is fixed, but the class of bug is not, and the inbox is the
 * wrong screen to lose.
 *
 * Deliberately a class component: `componentDidCatch` has no hook equivalent.
 */

import { Component } from 'react';
import { Warning } from '../Icons.jsx';

export default class TabBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Left in deliberately. Without it the only trace of a crashed tab is a
    // notice that says something went wrong and nothing about what.
    console.error(`Inbox tab "${this.props.name}" failed to render:`, error, info);
  }

  componentDidUpdate(previous) {
    // Switching tabs clears the failure, so a transient error does not leave
    // the tab permanently broken until a reload.
    if (previous.name !== this.props.name && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <section className="card">
        <div className="empty empty--inline">
          <div className="empty__art empty__art--warn">
            <Warning size={26} />
          </div>
          <h3>This tab could not be shown</h3>
          <p>
            Something in {this.props.name} failed to render. The rest of the inbox is unaffected —
            switch tabs and carry on. The details are in the browser console.
          </p>
          <p className="mono">{String(this.state.error?.message ?? this.state.error)}</p>
        </div>
      </section>
    );
  }
}
